/**
 * Admin "view-as-persona" PREVIEW persistence (#1383).
 *
 * The workspace switcher (#1369) lets an admin jump to any persona's home. This
 * adds a faithful PREVIEW on top of that wayfinding: while previewing, the whole
 * UI (sidebar nav, page-level visibility, money figures, admin-only surfaces)
 * masks down to exactly what that persona's REAL login would render -- so an
 * admin can SEE each permission type's experience.
 *
 * CRITICAL HONESTY: this is a FRONTEND visibility preview, NOT a server-side
 * privilege reduction. The admin's real token still holds full privileges; the
 * simulation only masks the UI. No server guard is weakened.
 *
 * The active persona key is persisted in localStorage (a plain string, read
 * synchronously by RoleRedirect's render-time <Navigate>) so the preview
 * survives route changes and reloads. There is ALWAYS an exit; exiting clears
 * this key and returns to full Admin.
 */

/**
 * The personas the admin may preview-as. These are exactly the non-admin
 * workspace-switcher destinations. "admin" is deliberately NOT a preview persona
 * -- it is the un-previewed, full-admin baseline (i.e. preview cleared).
 */
export type PreviewPersonaKey =
  | "crm"
  | "paralegal"
  // #1506: Finance and Accounts are ONE person at LWP -- a single combined
  // persona replaces the separate "finance" + "accounts" keys.
  | "finance_accounts"
  | "manager"
  | "office_admin"
  // D5-RBP-005 (#4175): the combined HR/Paralegal desk.
  | "hr_paralegal";

export const PREVIEW_PERSONA_KEYS: PreviewPersonaKey[] = [
  "crm",
  "paralegal",
  "finance_accounts",
  "manager",
  "office_admin",
  "hr_paralegal",
];

export const PREVIEW_PERSONA_STORAGE_KEY = "lwp_preview_persona";

function isPreviewPersonaKey(value: unknown): value is PreviewPersonaKey {
  return (
    typeof value === "string" &&
    (PREVIEW_PERSONA_KEYS as string[]).includes(value)
  );
}

/** Read the persisted preview persona, or null when not previewing / unavailable. */
export function getPreviewPersona(): PreviewPersonaKey | null {
  try {
    const stored = localStorage.getItem(PREVIEW_PERSONA_STORAGE_KEY);
    return isPreviewPersonaKey(stored) ? stored : null;
  } catch {
    // localStorage can throw in private-mode / sandboxed contexts.
    return null;
  }
}

/** Persist the active preview persona. Best-effort: swallow storage errors. */
export function setPreviewPersona(persona: PreviewPersonaKey): void {
  try {
    localStorage.setItem(PREVIEW_PERSONA_STORAGE_KEY, persona);
  } catch {
    // ignore -- preview persistence is a nicety, the exit always works.
  }
}

/** Clear the preview (return to full Admin). Best-effort. */
export function clearPreviewPersona(): void {
  try {
    localStorage.removeItem(PREVIEW_PERSONA_STORAGE_KEY);
  } catch {
    // ignore.
  }
}

/** Human label for the banner / switcher trigger. */
export function previewPersonaLabel(persona: PreviewPersonaKey): string {
  switch (persona) {
    case "crm":
      return "Agent CRM";
    case "paralegal":
      return "Aftersales/Paralegal";
    case "finance_accounts":
      return "Finance/Accounts";
    case "manager":
      return "Management";
    case "office_admin":
      return "Administration";
    case "hr_paralegal":
      return "HR/Paralegal";
  }
}
